import React, { useState, useContext } from "react";
import MilestoneTemplateList from "./MilestoneTemplateList";
import CreateMilestoneTemplateForm from "./CreateMilestoneTemplateForm";
import EditMilestoneTemplateForm from "./EditMilestoneTemplateForm";
import { AuthContext } from "../../contexts/AuthContext";

const MilestoneTemplatesPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [editTemplate, setEditTemplate] = useState(null);
  const { user } = useContext(AuthContext);

  // Check if user has admin role
  const isAdmin = user?.role?.toLowerCase() === "admin";

  const handleEditClick = (template) => {
    setEditTemplate(template);
    setActiveTab("edit");
  };

  const handleCancelEdit = () => {
    setEditTemplate(null);
    setActiveTab("list");
  };

  const handleCreateSuccess = () => {
    setActiveTab("list");
  };

  const handleUpdateSuccess = () => {
    setEditTemplate(null);
    setActiveTab("list");
  };

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Access Restricted</h4>
          <p>
            Milestone template management is only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="mb-4">
        <h1>Milestone Templates</h1>
        <p className="text-muted">
          Create and manage milestone templates for flat rate services. 
          Templates define the billing milestones (units) that can be applied to authorizations.
        </p>
      </div>

      {/* Info Card */}
      <div className="alert alert-info mb-4">
        <h5 className="alert-heading">
          <i className="bi bi-info-circle me-2"></i>
          How Milestone Templates Work
        </h5>
        <p className="mb-1">
          Milestone templates define a set of billing units for flat rate services. 
          When you create an authorization for a flat rate service, you can apply a template 
          to automatically create milestones.
        </p>
        <p className="mb-0">
          <strong>Example:</strong> A "Standard 3-Unit Program" template might include: 
          Unit 1 (Assessment), Unit 2 (Training), Unit 3 (Placement Support).
        </p>
      </div>

      {/* Tabs Navigation */}
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("list");
              setEditTemplate(null);
            }}
          >
            All Templates
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "create" ? "active" : ""}`}
            onClick={() => setActiveTab("create")}
            disabled={editTemplate !== null}
          >
            Create Template
          </button>
        </li>
        {editTemplate && (
          <li className="nav-item">
            <button className="nav-link active">
              Edit: {editTemplate.template_name}
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      <div className="tab-content mt-3">
        {activeTab === "list" && (
          <MilestoneTemplateList onEdit={handleEditClick} />
        )}

        {activeTab === "create" && (
          <CreateMilestoneTemplateForm onSuccess={handleCreateSuccess} />
        )}

        {activeTab === "edit" && editTemplate && (
          <EditMilestoneTemplateForm
            template={editTemplate}
            onCancel={handleCancelEdit}
            onUpdated={handleUpdateSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default MilestoneTemplatesPage;